import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { 
  Users, Calendar, CheckCircle, XCircle, AlertTriangle, TrendingUp, TrendingDown,
  ArrowUp, ArrowDown, Search, Filter, Download, ChevronUp, ChevronDown, Award, Target
} from 'lucide-react';
import './BackcheckDashboard.css';
import { AnimatedCard, AnimatedNumber, useAnimateOnMount } from '../common/AnimationUtils';

const BackcheckDashboard = ({ backcheckResults, onNavigate }) => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('summary');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  if (!backcheckResults) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '400px', textAlign: 'center',
        color: isDarkMode ? '#9CA3AF' : '#6B7280', padding: '2rem'
      }}>
        <AlertTriangle size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
        <h2 style={{ fontSize: '20px', marginBottom: '12px' }}>No Back-check Results Available</h2>
        <p style={{ fontSize: '13px', marginBottom: '12px' }}>
          Run back-check analysis to see results here
        </p>
        <button
          onClick={() => onNavigate?.('configure-back-checks')}
          style={{
            backgroundColor: '#500ee9ff', color: 'white', padding: '12px 24px',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
          }}
        >
          Configure Back-check Analysis
        </button>
      </div>
    );
  }

  const TabButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`ds-tab ${active ? 'active' : ''}`}
      style={{ color: isDarkMode ? '#E5E7EB' : '#111827' }}
    >
      {children}
    </button>
  );

  return (
    <div className='backchecks-dashboard' style={{ 
        minHeight: '100%', 
        //width: '93%',
        maxWidth: '100%',
        padding: isMobile ? '24px 12px 24px 12px' : '40px 60px 24px 60px',
        // marginLeft: '70px',
        backgroundColor: isDarkMode ? '#0D0E0E' : '#F5F5F599',
        // marginRight: '0px',
      }}>
      <div style={{
        marginBottom: '12px', 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: '500',
            color: isDarkMode ? '#9e9e9eff' : '#000000', 
            marginBottom: '20px',
            letterSpacing: '-0.5px',
          }}>
            Back-check Analysis Results
          </h1>
          <p style={{
            fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
            margin: 0, 
            marginBottom: '8px'
          }}>
            Comparison between primary survey and back-check responses
          </p>
        </div>
        {/*}
        <button
          onClick={() => onNavigate?.('configure-back-checks')}
          style={{
            backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff',
            color: isDarkMode ? '#9e9e9eff' : '#374151',
            padding: '10px 20px', 
            border: 'none', 
            borderRadius: '6px',
            cursor: 'pointer', 
            fontSize: '13px', 
            fontWeight: '500'
          }}
        >
          Back to Configuration
        </button>
        */}
      </div>

      <div className="ds-tabs-container" style={{
        marginBottom: '12px',
        marginTop: '4px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        <TabButton active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
          Summary
        </TabButton>
        <TabButton active={activeTab === 'enumerator'} onClick={() => setActiveTab('enumerator')}>
          Enumerator Stats
        </TabButton>
        {/* muted advanced analysis
        <TabButton active={activeTab === 'advanced'} onClick={() => setActiveTab('advanced')}>
          Advanced Analysis
        </TabButton>
        */}
        <TabButton active={activeTab === 'comparison'} onClick={() => setActiveTab('comparison')}>
          Comparison Details
        </TabButton>
      </div>

      <div>
        {activeTab === 'summary' && <SummaryTab data={backcheckResults} isDarkMode={isDarkMode} isMobile={isMobile} />}
        {activeTab === 'enumerator' && (
          <EnumeratorStatsTab 
            data={backcheckResults} isDarkMode={isDarkMode} isMobile={isMobile}
            sortConfig={sortConfig} setSortConfig={setSortConfig}
          />
        )}
        {activeTab === 'advanced' && <AdvancedAnalysisTab data={backcheckResults} isDarkMode={isDarkMode} isMobile={isMobile} />}
        {activeTab === 'comparison' && (
          <ComparisonTab 
            data={backcheckResults} isDarkMode={isDarkMode} isMobile={isMobile}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            filterType={filterType} setFilterType={setFilterType}
          />
        )}
      </div>
    </div>
  );
};

// SUMMARY TAB
const SummaryTab = ({ data, isDarkMode, isMobile }) => {

  const summary = data.overall_summary || data.summary || {};
  const quality = data.quality_indicators || {};
  const weighted = data.weighted_scores || {};
  
  const typeResults = {
    type1: data.type1_analysis || data.type1_results || {},
    type2: data.type2_analysis || data.type2_results || {},
    type3: data.type3_analysis || data.type3_results || {}
  };

  const totalBackchecks = summary.total_matched_respondents || 0;
  const totalPrimary = summary.total_primary_records || totalBackchecks;
  const backcheckRate = totalPrimary > 0 ? (totalBackchecks / totalPrimary) * 100 : 0;

  const MetricCard = ({ title, value, subtitle, color, Icon, badge, severity }) => {
    // Icon color driven by severity (matches Uniqueness MetricCard pattern)
    const iconColor = severity === 'critical'
      ? (isDarkMode ? '#f87171' : '#b91c1c')
      : severity === 'warning'
      ? (isDarkMode ? '#fbbf24' : '#b45309')
      : (isDarkMode ? '#60a5fa' : '#2563EB');
    const sevColor = severity ? iconColor : null;

    return (
    <div className="nsx-metric-card" style={{
      position: 'relative',
      padding: '18px',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
      borderRadius: '8px',
      border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
      overflow: 'hidden',
      fontFamily: "'Outfit', -apple-system, sans-serif",
    }}>
      {/* Severity corner bracket — SVG L-shape tracing the 8px rounded top-left corner */}
      {severity && (
        <svg
          width="30" height="30" viewBox="0 0 30 30"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={sevColor} strokeWidth="2" fill="none"
            strokeLinecap="round" opacity={0.9}
          />
        </svg>
      )}

      {/* Pulse beacon — top-right (hidden when badge is present to avoid overlap) */}
      {severity && !badge && (
        <div style={{
          position: 'absolute', top: '10px', right: '10px',
          width: '8px', height: '8px', borderRadius: '50%',
          background: sevColor,
          boxShadow: `0 0 6px ${sevColor}88`,
          animation: 'statusBreathe 2s ease-in-out infinite'
        }} />
      )}

      {/* Badge — e.g. WEIGHTED pill (top-right) */}
      {badge && (
        <div style={{
          position: 'absolute', top: '10px', right: '10px',
          backgroundColor: badge.color + '20', color: badge.color,
          padding: '3px 8px', borderRadius: '100px',
          fontSize: '10px', fontWeight: 500,
          letterSpacing: '0.06em', textTransform: 'uppercase'
        }}>
          {badge.text}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        {/* Icon chip — 36×36 with gradient */}
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>

        {/* Centered text block */}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: isDarkMode ? '#6B7280' : '#9CA3AF',
            marginBottom: '10px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {title}
          </div>
          <div
            className="metric-value"
            style={{
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: isDarkMode ? '#E2E8F0' : '#1F2937',
              lineHeight: 1.05,
              marginBottom: '6px',
            }}
          >
            {typeof value === 'number'
              ? <AnimatedNumber value={value} duration={800} />
              : value}
          </div>
          {subtitle && (
            <div style={{
              fontSize: '11.5px',
              fontWeight: 400,
              letterSpacing: '-0.005em',
              color: isDarkMode ? '#6B7280' : '#9CA3AF',
              lineHeight: 1.4,
            }}>
              {subtitle}
            </div>
          )}
        </div>

        {/* Phantom spacer — matches icon chip width so text column is truly card-centered */}
        <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
    );
  };

  const overallMatchRate = quality.overall_agreement_rate || 0;
  const confidenceInterval = quality.confidence_interval || [0, 0];

  const cardShadow = {
    boxShadow: isDarkMode
      ? "0 4px 6px rgba(0, 0, 0, 0)"
      : "0 1px 3px rgba(0, 0, 0, 0.08)",
    borderRadius: "100px",
    // backgroundColor: isDarkMode ? "#1E1E1E" : "#fff",
  };

  return (
    <div >
      {/* Overall Metrics */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
        gap: '16px', marginBottom: '32px'
      }}>
        <MetricCard
          title="Surveys Back-checked"
          value={summary.total_matched_respondents || 0}
          subtitle={`of ${summary.total_primary_records || 0} primary surveys`}
          color="#2B8AFF"
          Icon={Users}
          style={cardShadow}
        />
        <MetricCard
          title="Values Compared"
          value={(quality.total_comparisons || 0).toLocaleString()}
          subtitle="Across all variables"
          color="#8B5CF6"
          Icon={Calendar}
          style={cardShadow}
        />
        <MetricCard
          title="Back-check Rate"
          value={`${backcheckRate.toFixed(1)}%`}
          subtitle={`${totalBackchecks} of ${totalPrimary} surveys`}
          color="#06B6D4"
          Icon={CheckCircle}
          style={cardShadow}
        />
        <MetricCard
          title="Overall Match Rate"
          value={`${overallMatchRate.toFixed(1)}%`}
          subtitle={`95% CI: [${confidenceInterval[0]?.toFixed(1)}%, ${confidenceInterval[1]?.toFixed(1)}%]`}
          color={overallMatchRate >= 80 ? '#10B981' : '#F59E0B'}
          Icon={overallMatchRate >= 80 ? TrendingUp : TrendingDown}
          severity={overallMatchRate < 85 ? 'critical' : overallMatchRate < 95 ? 'warning' : null}
          style={cardShadow}
        />
        <MetricCard
          title="Weighted Quality Score"
          value={`${(weighted.weighted_quality_score || 0).toFixed(1)}%`}
          subtitle={weighted.interpretation || 'Based on error severity'}
          color={weighted.weighted_quality_score >= 90 ? '#10B981' : weighted.weighted_quality_score >= 75 ? '#F59E0B' : '#EF4444'}
          Icon={Award}
          severity={(weighted.weighted_quality_score || 0) < 75 ? 'critical' : (weighted.weighted_quality_score || 0) < 90 ? 'warning' : null}
          badge={{
            text: 'WEIGHTED',
            color: '#8B5CF6'
          }}
          style={cardShadow}
        />
        <MetricCard
          title="Quality Grade"
          value={quality.quality_grade || 'N/A'}
          subtitle="Overall assessment"
          color={
            quality.quality_grade === 'Excellent' ? '#10B981' :
            quality.quality_grade === 'Good' ? '#2B8AFF' :
            quality.quality_grade === 'Fair' ? '#F59E0B' : '#EF4444'
          }
          Icon={CheckCircle}
          severity={
            quality.quality_grade === 'Poor' || quality.quality_grade === 'Critical' ? 'critical' :
            quality.quality_grade === 'Fair' ? 'warning' : null
          }
          style={cardShadow}
        />
      </div>
      </AnimatedCard>

      {/* Weighted Error Breakdown */}
      {weighted.error_breakdown && (
        <AnimatedCard delay={80} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff',
          border: isDarkMode ? '1px solid #1a1a1dff' : '1px solid #E5E7EB',
          borderRadius: '8px', overflow: 'hidden', marginBottom: '32px'
        }}>
          <div style={{
            padding: '16px',
            borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
            backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff'
          }}>
            <h3 style={{
              fontSize: '13px', fontWeight: '600',
              color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
            }}>
              Weighted Error Analysis
            </h3>
            <p style={{
              fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
              margin: '4px 0 0 0'
            }}>
              Error weights: Type 1 (×10), Type 2 (×5), Type 3 (×2)
            </p>
          </div>
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
              {['type1', 'type2', 'type3'].map((type) => {
                const typeData = weighted.error_breakdown?.[type] || {};
                const weight = typeData.weight || 0;
                const rawErrors = typeData.raw_errors || 0;
                const weightedErrors = typeData.weighted_errors || 0;
                const comparisons = typeData.comparisons || 0;
                
                return (
                  <div key={type} style={{
                    padding: '16px', borderRadius: '8px',
                    backgroundColor: isDarkMode ? '#202122' : '#F9FAFB',
                    border: isDarkMode ? '0px solid #2e2e2eff' : '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      fontSize: '12px', fontWeight: '600',
                      color: type === 'type1' ? '#10B981' : type === 'type2' ? '#F59E0B' : '#6366F1',
                      marginBottom: '8px'
                    }}>
                      {type === 'type1' ? 'TYPE 1 (×10)' : type === 'type2' ? 'TYPE 2 (×5)' : 'TYPE 3 (×2)'}
                    </div>
                    <div style={{
                      fontSize: '13px', fontWeight: '600',
                      color: isDarkMode ? '#9e9e9eff' : '#111827',
                      marginBottom: '4px'
                    }}>
                      {weightedErrors}
                    </div>
                    <div style={{
                      fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280'
                    }}>
                      {rawErrors} raw errors × {weight}
                    </div>
                    <div style={{
                      fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
                      marginTop: '8px'
                    }}>
                      {comparisons.toLocaleString()} comparisons
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{
              marginTop: '16px', padding: '12px', borderRadius: '6px',
              backgroundColor: isDarkMode ? '#141414' : '#F3F4F6'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  Total Weighted Errors:
                </span>
                <span style={{
                  fontSize: '13px', fontWeight: '600',
                  color: isDarkMode ? '#9e9e9eff' : '#111827'
                }}>
                  {(weighted.total_weighted_errors || 0).toLocaleString()}
                </span>
              </div>
              <div style={{
                marginTop: '8px', fontSize: '12px',
                color: isDarkMode ? '#9CA3AF' : '#6B7280'
              }}>
                Max possible: {(weighted.max_possible_errors || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
        </AnimatedCard>
      )}

      {/* Type Breakdown Table */}
      <AnimatedCard delay={160} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: isDarkMode ? 'rgb(29, 29, 31)' : '#f5f5f5ff',
        border: isDarkMode ? '1px solid #1a1a1dff' : '1px solid #E5E7EB',
        borderRadius: '8px', overflow: 'hidden', marginBottom: '32px',
        boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
      }}>
        <div style={{
          padding: '16px',
          borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
          backgroundColor: isDarkMode ? 'rgb(27, 27, 27)' : '#f5f5f5ff'
        }}>
          <h3 style={{
            fontSize: '13px', fontWeight: '600',
            color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
          }}>
            Breakdown by Variable Type
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', }}>
            <thead style={{
              backgroundColor: isDarkMode ? 'rgb(30, 30, 31)' : '#F9FAFB',
              borderBottom: isDarkMode ? '0px solid #000000ff' : '2px solid #E5E7EB',
               fontSize: '13px'
            }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '450' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '450' }}>Variables</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Comparisons</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Differences</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Match Rate</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Confidence Interval</th>
              </tr>
            </thead>
            <tbody>
              {['type1', 'type2', 'type3'].map((type, idx) => {
                const typeData = typeResults[type];
                const typeSummary = typeData.type_summary || {};
                const variablesCount = typeData.variables_analyzed || 0;
                const comparisons = typeSummary.total_comparisons || 0;
                const differences = typeSummary.total_discrepancies || 0;
                const matchRate = typeSummary.overall_agreement_rate || 0;
                const ci = typeSummary.confidence_interval || [0, 0];
                
                return (
                  <tr
                    key={type}
                    style={{
                      borderBottom: idx < 2 ? (isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB') : 'none'
                    }}
                  >
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        backgroundColor: type === 'type1' ? '#10B98120' : type === 'type2' ? '#F59E0B20' : '#6366F120',
                        color: type === 'type1' ? '#10B981' : type === 'type2' ? '#F59E0B' : '#6366F1',
                        padding: '4px 8px', borderRadius: '4px', fontSize: '13px', fontWeight: '500'
                      }}>
                        {type === 'type1' ? 'Type 1 (Demographics)' :
                         type === 'type2' ? 'Type 2 (Complex)' : 'Type 3 (Stability)'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{variablesCount}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>{comparisons.toLocaleString()}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>{differences.toLocaleString()}</td>
                    <td style={{ 
                      padding: '12px', textAlign: 'right',
                      color: matchRate >= 90 ? '#10B981' : matchRate >= 70 ? '#F59E0B' : '#EF4444',
                      fontWeight: '600'
                    }}>
                      {matchRate.toFixed(1)}%
                    </td>
                    <td style={{
                      padding: '12px', textAlign: 'right', fontSize: '13px',
                      color: isDarkMode ? '#9CA3AF' : '#6B7280'
                    }}>
                      [{ci[0]?.toFixed(1)}%, {ci[1]?.toFixed(1)}%]
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </AnimatedCard>
      
      <AnimatedCard delay={240} translateY={12}>
      <ErrorRateTrendChart data={data} isDarkMode={isDarkMode} typeResults={typeResults} />
      </AnimatedCard>

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <AnimatedCard delay={320} translateY={12}>
        <div style={{
          backgroundColor: isDarkMode ? 'rgb(25, 25, 26)' : '#FFFFFF',
          border: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
          borderRadius: '8px', padding: '20px'
        }}>
          <h3 style={{
            fontSize: '13px', fontWeight: '600',
            color: isDarkMode ? '#9e9e9eff' : '#111827', marginBottom: '12px'
          }}>
            Recommendations
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {data.recommendations.map((rec, idx) => (
              <div
                key={idx}
                style={{
                  padding: '12px', backgroundColor: isDarkMode ? '#111827' : '#F9FAFB',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${
                    rec.priority === 'Critical' ? '#EF4444' :
                    rec.priority === 'High' ? '#F59E0B' : '#2B8AFF'
                  }`
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'
                }}>
                  <span style={{
                    fontSize: '12px', fontWeight: '600',
                    color: rec.priority === 'Critical' ? '#EF4444' :
                           rec.priority === 'High' ? '#F59E0B' : '#2B8AFF'
                  }}>
                    {rec.priority.toUpperCase()}
                  </span>
                  <span style={{
                    fontSize: '13px', fontWeight: '500',
                    color: isDarkMode ? '#9e9e9eff' : '#374151'
                  }}>
                    {rec.category}
                  </span>
                </div>
                <p style={{
                  fontSize: '13px', color: isDarkMode ? '#D1D5DB' : '#4B5563',
                  margin: '0 0 8px 0'
                }}>
                  <strong>Issue:</strong> {rec.issue}
                </p>
                <p style={{
                  fontSize: '13px', color: isDarkMode ? '#D1D5DB' : '#4B5563',
                  margin: 0
                }}>
                  <strong>Recommendation:</strong> {rec.recommendation}
                </p>
              </div>
            ))}
          </div>
        </div>
        </AnimatedCard>
      )}
    </div>
  );
};

// ERROR RATE TREND CHART
const ErrorRateTrendChart = ({ data, isDarkMode, typeResults }) => {
  const timelineData = data.temporal_analysis?.timeline_data || [];
  
  if (timelineData.length === 0) return null;

  const chartData = timelineData.map(item => ({
    date: item.date,
    total_error: item.discrepancy_rate || 0,
    type1_error: 100 - (typeResults.type1.type_summary?.overall_agreement_rate || 0),
    type2_error: 100 - (typeResults.type2.type_summary?.overall_agreement_rate || 0),
    type3_error: 100 - (typeResults.type3.type_summary?.overall_agreement_rate || 0)
  }));

  const maxError = Math.max(...chartData.map(d => Math.max(d.total_error, d.type1_error, d.type2_error, d.type3_error)));
  const yMax = Math.min(100, Math.ceil(maxError / 10) * 10 + 10);

  return (
    <div className="nsx-metric-card" style={{
      backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff',
      border: isDarkMode ? '1px solid #1a1a1dff' : '1px solid #E5E7EB',
      borderRadius: '8px', padding: '20px', marginBottom: '32px',
      boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
      marginBottom: '60px'
    }}>
      <h3 style={{
        fontSize: '13px', fontWeight: '600',
        color: isDarkMode ? '#9e9e9eff' : '#111827', marginBottom: '12px'
      }}>
        Error Rate Trends Over Time
      </h3>
      <div style={{ width: '100%', height: '400px', position: 'relative' }}>
        <svg width="100%" height="100%" viewBox="0 0 800 400">
          {[0, 25, 50, 75, 100].filter(v => v <= yMax).map(value => (
            <g key={value}>
              <line x1="60" y1={350 - (value / yMax * 300)} x2="780" y2={350 - (value / yMax * 300)}
                stroke={isDarkMode ? '#374151' : '#E5E7EB'} strokeWidth="1" strokeDasharray="5,5" />
              <text x="50" y={350 - (value / yMax * 300)} textAnchor="end" dominantBaseline="middle"
                fill={isDarkMode ? '#9CA3AF' : '#6B7280'} fontSize="12">{value}%</text>
            </g>
          ))}
          {chartData.map((d, i) => {
            const x = 80 + (i / (chartData.length - 1)) * 680;
            const showLabel = i % Math.ceil(chartData.length / 8) === 0 || i === chartData.length - 1;
            return showLabel ? (
              <text key={i} x={x} y="375" textAnchor="middle" fill={isDarkMode ? '#9CA3AF' : '#6B7280'} fontSize="11">
                {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </text>
            ) : null;
          })}
          <path d={chartData.map((d, i) => {
            const x = 80 + (i / (chartData.length - 1)) * 680;
            const y = 350 - (d.type1_error / yMax * 300);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ')} fill="none" stroke="#10B981" strokeWidth="2.5" />
          <path d={chartData.map((d, i) => {
            const x = 80 + (i / (chartData.length - 1)) * 680;
            const y = 350 - (d.type2_error / yMax * 300);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ')} fill="none" stroke="#F59E0B" strokeWidth="2.5" />
          <path d={chartData.map((d, i) => {
            const x = 80 + (i / (chartData.length - 1)) * 680;
            const y = 350 - (d.type3_error / yMax * 300);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ')} fill="none" stroke="#6366F1" strokeWidth="2.5" />
          <path d={chartData.map((d, i) => {
            const x = 80 + (i / (chartData.length - 1)) * 680;
            const y = 350 - (d.total_error / yMax * 300);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ')} fill="none" stroke="#DC2626" strokeWidth="3" />
          <text x="20" y="200" textAnchor="middle" fill={isDarkMode ? '#9e9e9eff' : '#374151'} fontSize="14" fontWeight="600"
            transform="rotate(-90 20 200)">Error Rate (%)</text>
          <text x="420" y="395" textAnchor="middle" fill={isDarkMode ? '#9e9e9eff' : '#374151'} fontSize="14" fontWeight="600">Date</text>
        </svg>
        <div className="nsx-metric-card" style={{
          position: 'absolute', top: '10px', right: '20px',
          backgroundColor: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
          border: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
          borderRadius: '6px', padding: '12px', display: 'flex',
          flexDirection: 'column', gap: '6px'
        }}>
          {[
            { label: 'Type 1', color: '#10B981' },
            { label: 'Type 2', color: '#F59E0B' },
            { label: 'Type 3', color: '#6366F1' },
            { label: 'Total', color: '#DC2626' }
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '20px', height: '3px', backgroundColor: item.color }} />
              <span style={{ fontSize: '12px', color: isDarkMode ? '#D1D5DB' : '#374151' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ENUMERATOR STATS TAB
const EnumeratorStatsTab = ({ data, isDarkMode, isMobile, sortConfig, setSortConfig }) => {

  const collectorAnalysis = data.data_collector_analysis || {};
  const collectorDetails = collectorAnalysis.collector_details || {};

  const enumeratorData = useMemo(() => {
    return Object.entries(collectorDetails).map(([id, stats]) => ({ id, ...stats }));
  }, [collectorDetails]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return enumeratorData;
    const sorted = [...enumeratorData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [enumeratorData, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  if (enumeratorData.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
        No enumerator statistics available
      </div>
    );
  }

  return (

    <AnimatedCard delay={0} translateY={12}>
    <div className="nsx-metric-card" style={{
      backgroundColor: isDarkMode ? '#1a1a1dff' : '#fcfcfcff',
      border: isDarkMode ? '1px solid #1a1a1dff' : '0px solid #E5E7EB',
      borderRadius: '8px', 
      overflowY: 'scroll', 
      maxHeight: '200vh',
      overflowX: 'hidden'
    }}>

      <div style={{
        padding: '16px',
        borderBottom: isDarkMode ? '1px solid #000000ff' : '1px solid #E5E7EB',
        backgroundColor: isDarkMode ? '#1a1a1dff' : '#F9FAFB',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>

        <h3 style={{
          fontSize: '13px', fontWeight: '600',
          color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
        }}>
          Enumerator Performance
        </h3>

        <div style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
          {enumeratorData.length} enumerators

        </div>

      </div>
      
      <div style={{ overflowX: 'auto', maxHeight: '1000px', overflowY: 'auto', overflowX: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead style={{
            backgroundColor: isDarkMode ? '#1a1a1dff' : '#F9FAFB',
            position: 'sticky', top: 0, zIndex: 100, fontSize: '13px'
          }}>
            <tr>
              <th onClick={() => handleSort('id')} style={{
                padding: '12px', textAlign: 'left', fontWeight: '450',
                cursor: 'pointer', userSelect: 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Enumerator ID
                  <SortIcon columnKey="id" />
                </div>
              </th>
              <th onClick={() => handleSort('weighted_quality_score')} style={{
                padding: '12px', textAlign: 'right', fontWeight: '450',
                cursor: 'pointer', userSelect: 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  Weighted Score
                  <SortIcon columnKey="weighted_quality_score" />
                </div>
              </th>
              <th onClick={() => handleSort('agreement_rate')} style={{
                padding: '12px', textAlign: 'right', fontWeight: '450',
                cursor: 'pointer', userSelect: 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  Match Rate
                  <SortIcon columnKey="agreement_rate" />
                </div>
              </th>
              <th onClick={() => handleSort('type1_error_rate')} style={{
                padding: '12px', textAlign: 'right', fontWeight: '450',
                cursor: 'pointer', userSelect: 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  Type 1 Error
                  <SortIcon columnKey="type1_error_rate" />
                </div>
              </th>
              <th onClick={() => handleSort('type2_error_rate')} style={{
                padding: '12px', textAlign: 'right', fontWeight: '450',
                cursor: 'pointer', userSelect: 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  Type 2 Error
                  <SortIcon columnKey="type2_error_rate" />
                </div>
              </th>
              <th onClick={() => handleSort('type3_error_rate')} style={{
                padding: '12px', textAlign: 'right', fontWeight: '450',
                cursor: 'pointer', userSelect: 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  Type 3 Error
                  <SortIcon columnKey="type3_error_rate" />
                </div>
              </th>
              <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>
                Confidence Interval
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((enum_stat, idx) => {
              const errorRate = enum_stat.discrepancy_rate || 0;
              const matchRate = enum_stat.agreement_rate || 0;
              const weightedScore = enum_stat.weighted_quality_score || 0;
              const ci = enum_stat.confidence_interval || [0, 0];
              const type1ErrorRate = enum_stat.type1_error_rate || 0;
              const type2ErrorRate = enum_stat.type2_error_rate || 0;
              const type3ErrorRate = enum_stat.type3_error_rate || 0;
              
              return (
                <tr key={enum_stat.id} style={{
                  borderBottom: idx < sortedData.length - 1 ? 
                    (isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB') : 'none',
                  backgroundColor: weightedScore < 75 ? (isDarkMode ? '#7F1D1D20' : '#FEE2E2') : 'transparent'
                }}>
                  <td style={{ padding: '12px', fontWeight: '500' }}>{enum_stat.id}</td>
                  <td style={{
                    padding: '12px', textAlign: 'right', fontWeight: '600',
                    color: weightedScore >= 90 ? '#10B981' : weightedScore >= 75 ? '#F59E0B' : '#EF4444'
                  }}>
                    {weightedScore.toFixed(1)}%
                  </td>
                  <td style={{
                    padding: '12px', textAlign: 'right', fontWeight: '600',
                    color: matchRate >= 90 ? '#10B981' : matchRate >= 70 ? '#F59E0B' : '#EF4444'
                  }}>
                    {matchRate.toFixed(1)}%
                  </td>
                  <td style={{
                    padding: '12px', textAlign: 'right',
                    color: type1ErrorRate > 5 ? '#EF4444' : type1ErrorRate > 2 ? '#F59E0B' : '#10B981',
                    fontWeight: '500'
                  }}>
                    {type1ErrorRate.toFixed(1)}%
                  </td>
                  <td style={{
                    padding: '12px', textAlign: 'right',
                    color: type2ErrorRate > 20 ? '#EF4444' : type2ErrorRate > 10 ? '#F59E0B' : '#10B981',
                    fontWeight: '500'
                  }}>
                    {type2ErrorRate.toFixed(1)}%
                  </td>
                  <td style={{
                    padding: '12px', textAlign: 'right',
                    color: type3ErrorRate > 10 ? '#EF4444' : type3ErrorRate > 5 ? '#F59E0B' : '#10B981',
                    fontWeight: '500'
                  }}>
                    {type3ErrorRate.toFixed(1)}%
                  </td>
                  <td style={{
                    padding: '12px', textAlign: 'right', fontSize: '12px',
                    color: isDarkMode ? '#9CA3AF' : '#6B7280'
                  }}>
                    [{ci[0]?.toFixed(1)}%, {ci[1]?.toFixed(1)}%]
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </AnimatedCard>
  );
};

// ADVANCED ANALYSIS TAB
const AdvancedAnalysisTab = ({ data, isDarkMode, isMobile }) => {
  const systematicBias = data.systematic_bias_analysis || {};
  const errorClustering = data.error_clustering || {};
  const weighted = data.weighted_scores || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Systematic Bias Detection */}
      <AnimatedCard delay={0} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: isDarkMode ? '#1a1a1dff' : '#fcfcfcff',
        border: isDarkMode ? '1px solid #1a1a1dff' : '0px solid #E5E7EB',
        borderRadius: '8px', overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px',
          borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
          backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff'
        }}>
          <h3 style={{
            fontSize: '13px', fontWeight: '600',
            color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
          }}>
            Systematic Bias Detection
          </h3>
          <p style={{
            fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
            margin: '4px 0 0 0'
          }}>
            Detects consistent over or under-reporting in numeric variables
          </p>
        </div>
        <div style={{ padding: '20px' }}>
          {systematicBias.summary ? (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px', marginBottom: '20px'
              }}>
                <div style={{
                  padding: '16px', borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#111827' : '#F9FAFB'
                }}>
                  <div style={{
                    fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
                    marginBottom: '4px'
                  }}>
                    Variables Analyzed
                  </div>
                  <div style={{
                    fontSize: '13px', fontWeight: '600',
                    color: isDarkMode ? '#9e9e9eff' : '#111827'
                  }}>
                    {systematicBias.summary.total_variables_analyzed || 0}
                  </div>
                </div>
                <div style={{
                  padding: '16px', borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#111827' : '#F9FAFB'
                }}>
                  <div style={{
                    fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
                    marginBottom: '4px'
                  }}>
                    Biased Variables
                  </div>
                  <div style={{
                    fontSize: '13px', fontWeight: '600',
                    color: systematicBias.summary.biased_variables_count > 0 ? '#EF4444' : '#10B981'
                  }}>
                    {systematicBias.summary.biased_variables_count || 0}
                  </div>
                </div>
                <div style={{
                  padding: '16px', borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#111827' : '#F9FAFB'
                }}>
                  <div style={{
                    fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
                    marginBottom: '4px'
                  }}>
                    Overall Status
                  </div>
                  <div style={{
                    fontSize: '13px', fontWeight: '600',
                    color: systematicBias.summary.overall_bias_detected ? '#EF4444' : '#10B981'
                  }}>
                    {systematicBias.summary.overall_bias_detected ? 'BIAS DETECTED' : 'NO BIAS'}
                  </div>
                </div>
              </div>

              {systematicBias.variable_analysis && Object.keys(systematicBias.variable_analysis).length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{
                      backgroundColor: isDarkMode ? '#1a1a1dff' : '#F9FAFB',
                      borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB'
                    }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Variable</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Mean Difference</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>% Bias</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Direction</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>P-value</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(systematicBias.variable_analysis).map(([varName, analysis], idx) => (
                        <tr key={varName} style={{
                          borderBottom: idx < Object.keys(systematicBias.variable_analysis).length - 1 ?
                            (isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB') : 'none',
                          backgroundColor: analysis.systematic_bias_detected && analysis.is_significant ?
                            (isDarkMode ? '#7F1D1D20' : '#FEE2E2') : 'transparent'
                        }}>
                          <td style={{ padding: '12px', fontWeight: '500' }}>{varName}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            {analysis.mean_difference?.toFixed(2)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            {analysis.percentage_bias?.toFixed(2)}%
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            <span style={{
                              padding: '4px 8px', borderRadius: '4px', fontSize: '12px',
                              backgroundColor: analysis.bias_direction === 'over-reporting' ? '#EF444420' :
                                             analysis.bias_direction === 'under-reporting' ? '#F59E0B20' : '#10B98120',
                              color: analysis.bias_direction === 'over-reporting' ? '#EF4444' :
                                    analysis.bias_direction === 'under-reporting' ? '#F59E0B' : '#10B981'
                            }}>
                              {analysis.bias_direction}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>
                            {analysis.statistical_significance?.toFixed(4)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {analysis.systematic_bias_detected && analysis.is_significant ? (
                              <XCircle size={18} color="#EF4444" />
                            ) : (
                              <CheckCircle size={18} color="#10B981" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
              No systematic bias analysis available
            </div>
          )}
        </div>
      </div>
      </AnimatedCard>

      {/* Error Clustering Analysis */}
      <AnimatedCard delay={80} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: isDarkMode ? '#1a1a1dff' : '#fcfcfcff',
        border: isDarkMode ? '1px solid #1a1a1dff' : '0px solid #E5E7EB',
        borderRadius: '8px', overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px',
          borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
          backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff'
        }}>
          <h3 style={{
            fontSize: '13px', fontWeight: '600',
            color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
          }}>
            Error Clustering Analysis
          </h3>
          <p style={{
            fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
            margin: '4px 0 0 0'
          }}>
            Chi-square test to detect if errors cluster with specific interviewers
          </p>
        </div>
        <div style={{ padding: '20px' }}>
          {errorClustering.summary ? (
            <>
              <div style={{
                padding: '20px', borderRadius: '8px',
                backgroundColor: errorClustering.is_significantly_clustered ?
                  (isDarkMode ? '#7F1D1D20' : '#FEE2E2') :
                  (isDarkMode ? '#10B98120' : '#D1FAE5'),
                border: `2px solid ${errorClustering.is_significantly_clustered ? '#EF4444' : '#10B981'}`,
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  marginBottom: '12px'
                }}>
                  {errorClustering.is_significantly_clustered ? (
                    <AlertTriangle size={24} color="#EF4444" />
                  ) : (
                    <CheckCircle size={24} color="#10B981" />
                  )}
                  <div style={{
                    fontSize: '13px', fontWeight: '600',
                    color: errorClustering.is_significantly_clustered ? '#EF4444' : '#10B981'
                  }}>
                    {errorClustering.interpretation}
                  </div>
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px', marginTop: '16px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280'
                    }}>
                      Chi-Square Statistic
                    </div>
                    <div style={{
                      fontSize: '13px', fontWeight: '600',
                      color: isDarkMode ? '#9e9e9eff' : '#111827'
                    }}>
                      {errorClustering.chi_square_statistic?.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280'
                    }}>
                      P-value
                    </div>
                    <div style={{
                      fontSize: '13px', fontWeight: '600',
                      color: errorClustering.p_value < 0.05 ? '#EF4444' : '#10B981'
                    }}>
                      {errorClustering.p_value?.toFixed(4)}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280'
                    }}>
                      Dispersion Index
                    </div>
                    <div style={{
                      fontSize: '13px', fontWeight: '600',
                      color: isDarkMode ? '#9e9e9eff' : '#111827'
                    }}>
                      {errorClustering.dispersion_index?.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{
                      fontSize: '12px', color: isDarkMode ? '#9CA3AF' : '#6B7280'
                    }}>
                      Number of Collectors
                    </div>
                    <div style={{
                      fontSize: '13px', fontWeight: '600',
                      color: isDarkMode ? '#9e9e9eff' : '#111827'
                    }}>
                      {errorClustering.summary.number_of_collectors}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                padding: '16px', borderRadius: '8px',
                backgroundColor: isDarkMode ? '#111827' : '#F9FAFB'
              }}>
                <h4 style={{
                  fontSize: '13px', fontWeight: '600',
                  color: isDarkMode ? '#9e9e9eff' : '#111827',
                  marginBottom: '12px'
                }}>
                  Statistical Summary
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      Total Errors:
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#9e9e9eff' : '#111827' }}>
                      {errorClustering.summary.total_errors}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      Mean Errors per Collector:
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#9e9e9eff' : '#111827' }}>
                      {errorClustering.summary.mean_errors_per_collector?.toFixed(1)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      Std Dev:
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#9e9e9eff' : '#111827' }}>
                      {errorClustering.summary.std_errors_per_collector?.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
              No error clustering analysis available
            </div>
          )}
        </div>
      </div>
      </AnimatedCard>

      {/* Weighted Scoring Explanation */}
      <AnimatedCard delay={160} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: isDarkMode ? '#1a1a1dff' : '#fcfcfcff',
        border: isDarkMode ? '1px solid #1a1a1dff' : '0px solid #E5E7EB',
        borderRadius: '8px', overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px',
          borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '0px solid #E5E7EB',
          backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff'
        }}>
          <h3 style={{
            fontSize: '13px', fontWeight: '600',
            color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
          }}>
            About Weighted Scoring
          </h3>
        </div>
        <div style={{ padding: '20px' }}>
          <p style={{
            fontSize: '13px', color: isDarkMode ? '#D1D5DB' : '#4B5563',
            marginBottom: '12px'
          }}>
            Unlike simple agreement rates, weighted scoring prioritizes critical error types.
            Not all errors are equal - mistakes in demographic data (Type 1) are far more serious
            than inconsistencies in attitudinal questions (Type 3).
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px', marginTop: '16px'
          }}>
            <div style={{
              padding: '16px', borderRadius: '8px',
              backgroundColor: isDarkMode ? '#111827' : '#F9FAFB',
              borderLeft: '4px solid #10B981'
            }}>
              <div style={{
                fontSize: '13px', fontWeight: '600',
                color: '#10B981', marginBottom: '8px'
              }}>
                Type 1 - Weight: 10×
              </div>
              <div style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                Demographics, IDs, names - must be nearly perfect
              </div>
            </div>
            <div style={{
              padding: '16px', borderRadius: '8px',
              backgroundColor: isDarkMode ? '#111827' : '#F9FAFB',
              borderLeft: '4px solid #F59E0B'
            }}>
              <div style={{
                fontSize: '13px', fontWeight: '600',
                color: '#F59E0B', marginBottom: '8px'
              }}>
                Type 2 - Weight: 5×
              </div>
              <div style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                Complex questions requiring calculation or recall
              </div>
            </div>
            <div style={{
              padding: '16px', borderRadius: '8px',
              backgroundColor: isDarkMode ? '#111827' : '#F9FAFB',
              borderLeft: '4px solid #6366F1'
            }}>
              <div style={{
                fontSize: '13px', fontWeight: '600',
                color: '#6366F1', marginBottom: '8px'
              }}>
                Type 3 - Weight: 2×
              </div>
              <div style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                Stable characteristics expected to remain consistent
              </div>
            </div>
          </div>
        </div>
      </div>
      </AnimatedCard>
    </div>
  );
};

// COMPARISON DETAILS TAB
const ComparisonTab = ({ data, isDarkMode, isMobile, searchTerm, setSearchTerm, filterType, setFilterType }) => {
  const [selectedVariable, setSelectedVariable] = useState(null);
  
  const type1Results = data.type1_analysis?.variable_results || {};
  const type2Results = data.type2_analysis?.variable_results || {};
  const type3Results = data.type3_analysis?.variable_results || {};
  
  const allVariableResults = {
    ...type1Results,
    ...type2Results,
    ...type3Results
  };

  const filteredVariables = useMemo(() => {
    let vars = Object.entries(allVariableResults);
    
    if (searchTerm) {
      vars = vars.filter(([name]) => 
        name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterType !== 'all') {
      const typeMap = {
        'type1': Object.keys(type1Results),
        'type2': Object.keys(type2Results),
        'type3': Object.keys(type3Results)
      };
      const allowedVars = typeMap[filterType] || [];
      vars = vars.filter(([name]) => allowedVars.includes(name));
    }
    
    return vars;
  }, [allVariableResults, searchTerm, filterType, type1Results, type2Results, type3Results]);

  return (
    <div>
      {/* Search and Filter Controls */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex', gap: '16px', marginBottom: '12px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute', left: '12px', top: '50%',
            transform: 'translateY(-50%)',
            color: isDarkMode ? '#9CA3AF' : '#6B7280'
          }} />
          <input
            type="text"
            placeholder="Search variables..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 40px',
              backgroundColor: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
              border: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
              borderRadius: '6px', fontSize: '13px',
              color: isDarkMode ? '#9e9e9eff' : '#111827'
            }}
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{
            padding: '10px 16px',
            backgroundColor: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
            border: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
            borderRadius: '6px', fontSize: '13px',
            color: isDarkMode ? '#9e9e9eff' : '#111827',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Types</option>
          <option value="type1">Type 1 Only</option>
          <option value="type2">Type 2 Only</option>
          <option value="type3">Type 3 Only</option>
        </select>
      </div>
      </AnimatedCard>

      {/* Variables List */}
      <AnimatedCard delay={80} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: isDarkMode ? '#1a1a1dff' : '#f5f5f5ff',
        border: isDarkMode ? '1px solid #1a1a1dff' : '1px solid #E5E7EB',
        borderRadius: '8px', overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px',
          borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
          backgroundColor: isDarkMode ? '#1a1a1dff' : '#F9FAFB'
        }}>
          <h3 style={{
            fontSize: '13px', fontWeight: '600',
            color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
          }}>
            Variable-Level Comparison
          </h3>
          <p style={{
            fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280',
            margin: '4px 0 0 0'
          }}>
            {filteredVariables.length} variables found
          </p>
        </div>
        <div style={{ overflowX: 'auto', maxHeight: '1000px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead style={{
              backgroundColor: isDarkMode ? '#1a1a1dff' : '#F9FAFB',
              position: 'sticky', top: 0, zIndex: 10,  fontSize: '13px'
            }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '450' }}>Variable</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '450' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Comparisons</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Discrepancies</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '450' }}>Agreement Rate</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '450' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVariables.map(([varName, varData], idx) => {
                const varType = Object.keys(type1Results).includes(varName) ? 'type1' :
                               Object.keys(type2Results).includes(varName) ? 'type2' : 'type3';
                const agreementRate = varData.agreement_rate || 0;
                
                return (
                  <tr key={varName} style={{
                    borderBottom: idx < filteredVariables.length - 1 ?
                      (isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB') : 'none'
                  }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>{varName}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        backgroundColor: varType === 'type1' ? '#10B98120' :
                                       varType === 'type2' ? '#F59E0B20' : '#6366F120',
                        color: varType === 'type1' ? '#10B981' :
                              varType === 'type2' ? '#F59E0B' : '#6366F1',
                        padding: '4px 8px', borderRadius: '4px',
                        fontSize: '11px', fontWeight: '600'
                      }}>
                        {varType.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {(varData.valid_comparisons || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {(varData.discrepancies || 0).toLocaleString()}
                    </td>
                    <td style={{
                      padding: '12px', textAlign: 'right', fontWeight: '600',
                      color: agreementRate >= 90 ? '#10B981' :
                            agreementRate >= 70 ? '#F59E0B' : '#EF4444'
                    }}>
                      {agreementRate.toFixed(1)}%
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => setSelectedVariable(varName)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: isDarkMode ? '#1a1a1dff' : '#F3F4F6',
                          border: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB',
                          borderRadius: '4px', fontSize: '12px',
                          color: isDarkMode ? '#9e9e9eff' : '#374151',
                          cursor: 'pointer', fontWeight: '500'
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      </AnimatedCard>

      {/* Variable Detail Modal */}
      {selectedVariable && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          padding: '20px'
        }} onClick={() => setSelectedVariable(null)}>
          <div className="nsx-metric-card" style={{
            backgroundColor: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
            borderRadius: '8px', maxWidth: '900px', width: '100%',
            maxHeight: '80vh', overflow: 'auto', padding: '18px'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '20px'
            }}>
              <h3 style={{
                fontSize: '13px', fontWeight: '600',
                color: isDarkMode ? '#9e9e9eff' : '#111827', margin: 0
              }}>
                {selectedVariable} - Detailed Analysis
              </h3>
              <button
                onClick={() => setSelectedVariable(null)}
                style={{
                  padding: '8px', backgroundColor: 'transparent',
                  border: 'none', cursor: 'pointer',
                  color: isDarkMode ? '#9CA3AF' : '#6B7280'
                }}
              >
                <XCircle size={24} />
              </button>
            </div>
            
            {allVariableResults[selectedVariable]?.discrepancy_examples && 
             allVariableResults[selectedVariable].discrepancy_examples.length > 0 && (
              <div>
                <h4 style={{
                  fontSize: '13px', fontWeight: '600',
                  color: isDarkMode ? '#9e9e9eff' : '#111827',
                  marginBottom: '12px'
                }}>
                  Discrepancy Examples
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{
                      backgroundColor: isDarkMode ? '#1a1a1dff' : '#F9FAFB'
                    }}>
                      <tr>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Respondent ID</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Primary Value</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Backcheck Value</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Enumerator</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Survey Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allVariableResults[selectedVariable].discrepancy_examples.map((example, idx) => (
                        <tr key={idx} style={{
                          borderBottom: isDarkMode ? '1px solid #2e2e2eff' : '1px solid #E5E7EB'
                        }}>
                          <td style={{ padding: '10px' }}>{example.respondent_id}</td>
                          <td style={{ padding: '10px', fontWeight: '500' }}>{example.primary_value}</td>
                          <td style={{ padding: '10px', fontWeight: '500', color: '#EF4444' }}>
                            {example.backcheck_value}
                          </td>
                          <td style={{ padding: '10px' }}>{example.enumerator}</td>
                          <td style={{ padding: '10px', fontSize: '12px' }}>{example.survey_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BackcheckDashboard;